/**
 * WORKFLOW SELECTOR
 *
 * Shared dropdown for selecting a workflow in task creation flows.
 * Two sections: Colony Agent (always first, highlighted) and Custom Workflows.
 */

import { useEffect, useRef, useState, useMemo } from "react";
import { Check, ChevronDown, Sparkles } from "lucide-react";

// ─── Constants ──────────────────

export const COLONY_AGENT_SENTINEL = "__colony_agent__";

export function isColonyAgent(id: string): boolean {
  return id === COLONY_AGENT_SENTINEL;
}

export const FALLBACK_WORKFLOW_IDS = new Set(["wf-bug-default", "wf-feature-default"]);

const FALLBACK_WORKFLOW_LABELS: Record<string, string> = {
  "wf-bug-default": "Bug Fix (Default)",
  "wf-feature-default": "Feature (Default)",
};

export function getWorkflowDisplayName(workflow: { id: string; name: string }): string {
  return FALLBACK_WORKFLOW_LABELS[workflow.id] ?? workflow.name;
}

/**
 * Maps workflow issueType to the Task model's issueType union.
 * Task allows: "wp" | "bug" | "feature" | "hotfix".
 * Workflow allows additional types like "refactor" which maps to "wp".
 */
export function workflowIssueTypeToTaskIssueType(
  issueType: string,
): "wp" | "bug" | "feature" | "hotfix" {
  if (issueType === "bug") return "bug";
  if (issueType === "hotfix") return "hotfix";
  if (issueType === "wp" || issueType === "refactor") return "wp";
  return "feature";
}

/**
 * Filters and sorts workflows for display in the selector.
 * Prefers custom workflows; falls back to all (including defaults) if none.
 * Sorted by most recently updated first.
 */
export function filterSavedWorkflows<T extends { id: string; updatedAt?: string; createdAt?: string }>(
  workflows: T[],
): T[] {
  const custom = workflows.filter((w) => !FALLBACK_WORKFLOW_IDS.has(w.id));
  const source = custom.length > 0 ? custom : workflows;
  return [...source].sort((a, b) => {
    const aTs = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bTs = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bTs - aTs;
  });
}

// ─── Types ──────────────────

export interface WorkflowOption {
  id: string;
  name: string;
  issueType?: string;
}

interface WorkflowSelectorProps {
  value: string;
  onChange: (value: string) => void;
  workflows: WorkflowOption[];
  placeholder?: string;
}

// ─── Component ──────────────────

export function WorkflowSelector({
  value,
  onChange,
  workflows,
  placeholder = "Select workflow...",
}: WorkflowSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(() => {
    if (isColonyAgent(value)) return "Colony Agent (Automatic)";
    const wf = workflows.find((w) => w.id === value);
    return wf ? getWorkflowDisplayName(wf) : "";
  }, [value, workflows]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`
          flex items-center gap-2.5 w-full h-9 px-3 rounded-[6px]
          text-[13px] text-left transition-all duration-150 cursor-pointer
          border outline-none
          ${open
            ? "border-[color:var(--colony-brand)] shadow-[0_0_0_2px_var(--colony-brand-glow)]"
            : "border-[var(--colony-border)] hover:border-[var(--colony-border-hover)]"
          }
        `}
        style={{
          background: "var(--colony-bg)",
          boxShadow: open ? undefined : "var(--shadow-inset)",
          transitionTimingFunction: "var(--ease-out-expo)",
        }}
      >
        <span className="shrink-0">
          {isColonyAgent(value)
            ? <img src="/colony-logo.png" alt="Colony" className="w-4 h-4" />
            : <Sparkles size={14} strokeWidth={1.5} className="text-colony-text-tertiary" />
          }
        </span>
        <span className={`flex-1 truncate ${selectedLabel ? "text-colony-text-primary" : "text-colony-text-tertiary"}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          className={`text-colony-text-tertiary shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 z-50 rounded-[8px] overflow-hidden max-h-[320px] overflow-y-auto colony-scrollbar"
          style={{
            background: "var(--colony-surface-elevated)",
            border: "1px solid var(--colony-border)",
            boxShadow: "var(--colony-shadow-elevated)",
            backdropFilter: "var(--glass-blur)",
            animation: "colony-fadeIn 120ms var(--ease-out-expo)",
          }}
        >
          <div className="py-1">
            {/* ─── Colony Agent (Automatic) ─── */}
            <button
              onClick={() => { onChange(COLONY_AGENT_SENTINEL); setOpen(false); }}
              className={`
                flex items-start gap-2.5 w-full px-3 py-2.5 text-left transition-colors cursor-pointer
                ${isColonyAgent(value)
                  ? "bg-[color:var(--colony-brand-muted)]"
                  : "hover:bg-[rgba(255,255,255,0.04)]"
                }
              `}
            >
              <div
                className="flex items-center justify-center w-7 h-7 rounded-[6px] shrink-0 mt-0.5"
                style={{
                  background: "var(--colony-brand-muted)",
                  border: "1px solid rgba(0, 178, 200, 0.15)",
                }}
              >
                <img src="/colony-logo.png" alt="Colony" className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-colony-text-primary">Colony Agent</span>
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-[4px]"
                    style={{
                      background: "var(--colony-brand-muted)",
                      color: "var(--colony-brand)",
                    }}
                  >
                    Automatic
                  </span>
                </div>
                <p className="text-[11px] text-colony-text-tertiary mt-0.5 leading-[1.4]">
                  Analyzes your task and picks the right process automatically
                </p>
              </div>
              {isColonyAgent(value) && (
                <Check size={13} className="shrink-0 mt-2" style={{ color: "var(--colony-brand)" }} />
              )}
            </button>

            {/* ─── Custom Workflows ─── */}
            {workflows.length > 0 && (
              <>
                <div className="mx-3 my-1" style={{ height: 1, background: "var(--colony-border)" }} />
                <div className="px-3 py-1.5">
                  <span className="text-[10px] font-semibold text-colony-text-tertiary uppercase tracking-[0.05em]">
                    Custom Workflows
                  </span>
                </div>
              </>
            )}

            {workflows.map((workflow) => (
              <button
                key={workflow.id}
                onClick={() => { onChange(workflow.id); setOpen(false); }}
                className={`
                  flex items-center gap-2.5 w-full h-8 px-3 text-left transition-colors cursor-pointer
                  ${value === workflow.id
                    ? "bg-[color:var(--colony-brand-muted)] text-colony-text-primary"
                    : "text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)]"
                  }
                `}
              >
                <span className="flex-1 text-[13px] truncate">{getWorkflowDisplayName(workflow)}</span>
                {workflow.issueType && (
                  <span className="text-[11px] text-colony-text-tertiary shrink-0">{workflow.issueType}</span>
                )}
                {value === workflow.id && (
                  <Check size={13} className="shrink-0" style={{ color: "var(--colony-brand)" }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
